import { Check, CheckCheck, Camera, Mic, Smile } from "lucide-react";

interface WhatsAppPhonePreviewProps {
  headerType?: string;
  headerText?: string;
  bodyText: string;
  footerText?: string;
  buttons?: any[];
  exampleValues?: string[];
}

export function WhatsAppPhonePreview({
  headerType,
  headerText,
  bodyText,
  footerText,
  buttons,
  exampleValues = [],
}: WhatsAppPhonePreviewProps) {
  const renderTextWithVariables = (text: string) => {
    if (!text) return '';
    let result = text;
    exampleValues.forEach((value, index) => {
      if (value) {
        const placeholder = `{{${index + 1}}}`;
        result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
      }
    });
    return result;
  };

  const now = new Date();
  const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  return (
    <div className="mx-auto w-full max-w-[320px]">
      {/* Phone frame */}
      <div className="rounded-[2rem] border-[3px] border-foreground/20 bg-foreground/5 overflow-hidden shadow-xl">
        {/* Status bar */}
        <div className="bg-[#075e54] px-4 py-2 flex items-center justify-between">
          <span className="text-[10px] text-white/80">{timeStr}</span>
          <div className="flex gap-1">
            <div className="w-3 h-3 rounded-full bg-white/30" />
            <div className="w-3 h-3 rounded-full bg-white/30" />
          </div>
        </div>

        {/* Chat header */}
        <div className="bg-[#075e54] px-3 pb-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <span className="text-white text-xs font-bold">T</span>
          </div>
          <div>
            <p className="text-white text-sm font-medium">Template Preview</p>
            <p className="text-white/60 text-[10px]">online</p>
          </div>
        </div>

        {/* Chat area */}
        <div
          className="min-h-[380px] p-3 flex flex-col justify-end"
          style={{
            backgroundColor: '#ece5dd',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        >
          {/* Message bubble */}
          <div className="max-w-[85%] ml-auto">
            <div className="bg-[#dcf8c6] rounded-lg rounded-tr-none p-2.5 shadow-sm relative">
              {/* Header */}
              {headerType === 'TEXT' && headerText && (
                <p className="font-semibold text-[13px] text-gray-900 mb-1">
                  {renderTextWithVariables(headerText)}
                </p>
              )}
              {headerType === 'IMAGE' && (
                <div className="w-full h-28 bg-gray-300 rounded mb-2 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-gray-500" />
                </div>
              )}
              {headerType === 'VIDEO' && (
                <div className="w-full h-28 bg-gray-300 rounded mb-2 flex items-center justify-center">
                  <span className="text-xs text-gray-500">▶ Vídeo</span>
                </div>
              )}
              {headerType === 'DOCUMENT' && (
                <div className="w-full h-16 bg-gray-200 rounded mb-2 flex items-center justify-center">
                  <span className="text-xs text-gray-500">📄 Documento</span>
                </div>
              )}

              {/* Body */}
              <p className="text-[13px] text-gray-900 whitespace-pre-wrap leading-snug">
                {renderTextWithVariables(bodyText)}
              </p>

              {/* Footer */}
              {footerText && (
                <p className="text-[11px] text-gray-500 mt-1">{footerText}</p>
              )}

              {/* Time + checkmarks */}
              <div className="flex items-center justify-end gap-1 mt-1">
                <span className="text-[10px] text-gray-500">{timeStr}</span>
                <CheckCheck className="w-3.5 h-3.5 text-[#53bdeb]" />
              </div>
            </div>

            {/* Quick reply buttons */}
            {buttons && buttons.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {buttons.map((button: any, index: number) => (
                  <button
                    key={index}
                    className="flex-1 min-w-[45%] bg-white rounded-lg py-2 px-3 text-[13px] text-[#00a884] font-medium shadow-sm text-center hover:bg-gray-50 transition-colors"
                  >
                    {button.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Input bar */}
        <div className="bg-[#f0f0f0] px-2 py-2 flex items-center gap-2">
          <Smile className="w-5 h-5 text-gray-500 shrink-0" />
          <div className="flex-1 bg-white rounded-full px-3 py-1.5">
            <span className="text-xs text-gray-400">Digite uma mensagem...</span>
          </div>
          <Mic className="w-5 h-5 text-gray-500 shrink-0" />
        </div>
      </div>
    </div>
  );
}
